<template>
  <teleport to="body">
    <div v-if="modelValue" class="pv-overlay" role="dialog" aria-modal="true" @click.self="close">
      <!-- Top bar: title · (source/street-view toggle) · box toggle · download · close -->
      <div class="pv-bar">
        <div class="pv-title">
          <span class="pv-title-main">{{ title || 'Source image' }}</span>
          <span v-if="activeFileName" class="pv-file">{{ activeFileName }}</span>
        </div>

        <div v-if="is360" class="pv-tabs" role="tablist">
          <button type="button" class="pv-tab" :class="{ active: mode === 'source' }" @click="mode = 'source'">
            <v-icon size="16">mdi-image-outline</v-icon><span>Full image</span>
          </button>
          <button type="button" class="pv-tab" :class="{ active: mode === 'equirect' }" @click="show360">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <svg class="pv-peg" viewBox="0 0 44 68" width="12" height="18" v-html="pegmanSvg"></svg>
            <span>Street view</span>
          </button>
        </div>

        <div class="pv-bar-actions">
          <button type="button" class="pv-icon-btn" :class="{ active: showBox }"
            title="Show or hide the marker" @click="showBox = !showBox">
            <v-icon size="18">{{ showBox ? 'mdi-image-filter-center-focus' : 'mdi-image-filter-center-focus-weak' }}</v-icon>
            <span>Marker</span>
          </button>
          <button type="button" class="pv-icon-btn" :disabled="!activeUrl || downloading" title="Download this image"
            @click="downloadActive">
            <v-progress-circular v-if="downloading" indeterminate size="16" width="2" color="#64ffda" />
            <v-icon v-else size="18">mdi-tray-arrow-down</v-icon>
          </button>
          <button type="button" class="pv-close" aria-label="Close" @click="close">
            <v-icon size="22">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- Stage -->
      <div ref="stage" class="pv-stage" :class="{ 'is-zoomed': zoom > 1, dragging }"
        @wheel.prevent="onWheel" @mousedown="onDragStart" @mousemove="onDragMove"
        @mouseup="onDragEnd" @mouseleave="onDragEnd">
        <!-- Source frame with the detection box -->
        <template v-if="mode === 'source'">
          <div v-if="sourceUnavailable" class="pv-empty">
            <v-icon size="40">mdi-image-off-outline</v-icon>
            <span>Source image unavailable.</span>
          </div>
          <div v-else class="pv-card">
            <!-- Shimmer placeholder while the URL resolves + the image downloads. -->
            <div v-if="!sourceImgReady" class="pv-shimmer"></div>
            <div v-show="sourceImgReady" class="pv-imgwrap" :style="zoomStyle">
              <img v-if="sourceUrl" :src="sourceUrl" class="pv-img" alt="Source frame" draggable="false"
                @load="onSourceLoad" @error="onSourceError" />
              <template v-if="showBox">
                <div v-for="(b, i) in wrapStyles(bboxStyle)" :key="i" class="pv-bbox"
                  :class="{ 'pv-bbox--split': b.split }" :style="b.style"></div>
              </template>
            </div>
          </div>
        </template>

        <!-- Parent 360 panorama -->
        <template v-else>
          <div v-if="equirectLoading" class="pv-empty">
            <v-progress-circular indeterminate color="#66c7b4" size="30" width="2" />
            <span>Loading panorama…</span>
          </div>
          <div v-else-if="equirectUrl" class="pv-card">
            <div class="pv-imgwrap" :style="zoomStyle">
              <img :src="equirectUrl" class="pv-img" alt="360 panorama" draggable="false" />
              <template v-if="showBox">
                <div v-for="(b, i) in wrapStyles(equirectBoxStyle)" :key="i" class="pv-bbox"
                  :class="{ 'pv-bbox--split': b.split }" :style="b.style"></div>
              </template>
            </div>
          </div>
          <div v-else class="pv-empty">
            <v-icon size="40">mdi-panorama-outline</v-icon>
            <span>Panorama unavailable for this capture.</span>
          </div>
        </template>

        <!-- Zoom controls -->
        <div v-if="activeUrl" class="pv-zoom" @mousedown.stop @wheel.stop>
          <button type="button" class="pv-zoom-btn" title="Zoom out" :disabled="zoom <= 1" @click="zoomBy(1 / 1.3)">
            <v-icon size="18">mdi-minus</v-icon>
          </button>
          <button type="button" class="pv-zoom-level" title="Reset zoom" @click="resetZoom">{{ Math.round(zoom * 100) }}%</button>
          <button type="button" class="pv-zoom-btn" title="Zoom in" :disabled="zoom >= 8" @click="zoomBy(1.3)">
            <v-icon size="18">mdi-plus</v-icon>
          </button>
        </div>
      </div>

      <div class="pv-hint">{{ hint }}</div>

      <!-- Download choice: save the image plain, or with the marker box burned onto the pixels. -->
      <div v-if="downloadAsk" class="pv-ask" @click.self="downloadAsk = false">
        <div class="pv-ask-card">
          <div class="pv-ask-title">Include the marker box?</div>
          <p class="pv-ask-sub">Draw the detection outline onto the downloaded image.</p>
          <div class="pv-ask-actions">
            <button type="button" class="pv-ask-btn" @click="doDownload(false)">Image only</button>
            <button type="button" class="pv-ask-btn primary" @click="doDownload(true)">With box</button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { PEGMAN_SVG } from '@/lib/pegmanSvg';
import { equirectBoxFromFace } from '@/lib/equirectProjection';

export default {
  name: 'ProvenanceImageOverlay',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    sourceUrl: { type: String, default: null },
    // The parent is still resolving the source URL — show the shimmer, not "unavailable".
    sourceLoading: { type: Boolean, default: false },
    sourceFileName: { type: String, default: null },
    // Detection box in SOURCE-image pixels, top-left origin: { left, top, width, height }.
    bbox: { type: Object, default: null },
    bboxLabel: { type: String, default: '' },
    bboxConfidence: { type: Number, default: null },   // 0..1 (or 0..100); shown in the window header
    // External reference (work-order / CMMS id). When set, used as the download filename and drawn
    // under the box on a "with box" download.
    referenceId: { type: String, default: null },
    is360: { type: Boolean, default: false },
    // Force the RAW detection box (not the square DetectionCropper crop) — used for a stitched legacy
    // panorama (a 360° strip / equirect), whose box is already in the stitched image's own pixels.
    rawBox: { type: Boolean, default: false },
    equirectUrl: { type: String, default: null },
    equirectLoading: { type: Boolean, default: false },
    // Which view to open on: 'source' (default) or 'equirect' (the 360 / street view).
    startMode: { type: String, default: 'source' },
  },
  emits: ['update:modelValue', 'request-equirect'],
  data() {
    return {
      mode: 'source',        // 'source' | 'equirect'
      showBox: true,         // detection-box overlay toggle (top bar)
      natW: 0,               // natural source frame size (for the source bbox + the equirect mapping)
      natH: 0,
      sourceImgReady: false, // the source <img> has loaded (shimmer hides, box shows)
      sourceFailed: false,   // the source <img> errored
      downloading: false,
      downloadAsk: false,    // show the "include the marker box?" choice before downloading
      equirectBox: null,     // {left,top,width,height} 0..1 — the crop box mapped onto the equirect
      pegmanSvg: PEGMAN_SVG,
      zoom: 1,               // 1..8 — wheel / +/- zoom of the displayed image
      panX: 0, panY: 0,      // pan offset in screen px (applied before scale)
      dragging: false,
      dragLastX: 0, dragLastY: 0,
    };
  },
  computed: {
    // Show "unavailable" only once we KNOW there's nothing to show — the parent finished resolving and
    // gave no URL, or the image failed. While resolving/loading we shimmer instead.
    sourceUnavailable() {
      return this.sourceFailed || (!this.sourceLoading && !this.sourceUrl);
    },
    // Transform for the image + its box together, so the highlight tracks the zoom/pan.
    zoomStyle() {
      return {
        transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`,
        transformOrigin: 'center center',
      };
    },
    activeUrl() {
      return this.mode === 'equirect' ? this.equirectUrl : this.sourceUrl;
    },
    activeFileName() {
      return this.mode === 'equirect' ? '360 panorama' : this.sourceFileName;
    },
    hint() {
      if (this.mode === 'equirect') return 'The 360 capture this detection came from.';
      return 'The highlighted box is where the model detected this finding.';
    },
    // The exact SQUARE region the marker thumbnail was cut from, in source-image pixels. Mirrors the
    // inference service's DetectionCropper: a square max(w,h)*1.3 around the detection, floored at
    // 250px, never larger than the frame, slid fully inside. Outlining this matches the thumbnail.
    cropRect() {
      if (!this.bbox || !this.natW || !this.natH) return null;
      const w = this.bbox.width, h = this.bbox.height;
      if (w <= 0 || h <= 0) return null;
      const cx = this.bbox.left + w / 2, cy = this.bbox.top + h / 2;
      const PADDING_FRACTION = 0.15, MIN_CROP = 250;
      const padded = Math.max(w * (1 + 2 * PADDING_FRACTION), h * (1 + 2 * PADDING_FRACTION));
      let side = Math.ceil(Math.max(padded, MIN_CROP));
      side = Math.min(side, Math.min(this.natW, this.natH));
      if (side <= 0) return null;
      let left = Math.round(cx - side / 2), top = Math.round(cy - side / 2);
      left = Math.min(Math.max(left, 0), this.natW - side);
      top = Math.min(Math.max(top, 0), this.natH - side);
      return { left, top, side };
    },
    // True when the source IS an equirect panorama (a re-captured 360 marker), not a cube face / mono
    // frame. Its stored box is the drawn detection region itself, so we outline it raw.
    isEquirectSource() {
      return this.rawBox || /equirect|pano|(^|[_-])360([_-]|\.|$)/i.test(this.sourceFileName || '');
    },
    // The box outline on the source image. For a cube/mono frame it's the DetectionCropper square (so it
    // matches the thumbnail crop); for an equirect it's the raw detection box (the square crop would
    // balloon across the wide panorama).
    bboxStyle() {
      const pct = (n, d) => `${(n / d) * 100}%`;
      if (this.isEquirectSource) {
        const b = this.bbox;
        if (!b || !this.natW || !this.natH) return null;
        return {
          left: pct(b.left, this.natW),
          top: pct(b.top, this.natH),
          width: pct(b.width, this.natW),
          height: pct(b.height, this.natH),
        };
      }
      const c = this.cropRect;
      if (!c) return null;
      return {
        left: pct(c.left, this.natW),
        top: pct(c.top, this.natH),
        width: pct(c.side, this.natW),
        height: pct(c.side, this.natH),
      };
    },
    // The crop box mapped onto the equirect, derived from the source face's REAL camera pose
    // (computeEquirectBox) rather than assumed cube axes. Level horizon.
    equirectBoxStyle() {
      const b = this.equirectBox;
      if (!b) return null;
      return {
        left: `${b.left * 100}%`,
        top: `${b.top * 100}%`,
        width: `${b.width * 100}%`,
        height: `${b.height * 100}%`,
      };
    },
    faceName() {
      const m = /_cube_([a-z]+)/i.exec(this.sourceFileName || '');
      return m ? m[1].toLowerCase() : null;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.mode = (this.is360 && this.startMode === 'equirect') ? 'equirect' : 'source';
        this.showBox = true;
        this.natW = this.natH = 0;
        this.sourceImgReady = false;
        this.sourceFailed = false;
        this.resetZoom();
        this.preloadSourceDims();   // so the equirect box can be mapped even if source isn't shown
        if (this.mode === 'equirect' && !this.equirectUrl && !this.equirectLoading) {
          this.$emit('request-equirect');
        }
        window.addEventListener('keydown', this.onKey);
      } else {
        window.removeEventListener('keydown', this.onKey);
      }
    },
    sourceUrl() {
      // New URL (e.g. the parent just resolved it) → reset the load state so the shimmer shows until it
      // decodes, then preload dims for the box mapping.
      this.sourceImgReady = false;
      this.sourceFailed = false;
      if (this.modelValue) this.preloadSourceDims();
    },
    // Recompute the equirect box once the source dims are known, or when the view/box changes.
    natW() { this.computeEquirectBox(); },
    mode(m) { this.resetZoom(); if (m === 'equirect') this.computeEquirectBox(); },
    bbox() { this.computeEquirectBox(); },
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
    },
    onKey(e) {
      if (e.key === 'Escape') this.close();
    },
    // A box whose left+width spills past the right edge straddles the panorama's ±180° fold — render it
    // as TWO pieces (right edge + wrapped remainder on the left) instead of one overflowing rectangle.
    // The split pieces drop the spotlight dimming (two 9999px surrounds would darken each other).
    wrapStyles(style) {
      if (!style) return [];
      const left = parseFloat(style.left), width = parseFloat(style.width);
      if (!isFinite(left) || !isFinite(width) || left + width <= 100) return [{ style, split: false }];
      return [
        { style: { ...style, width: `${100 - left}%` }, split: true },
        { style: { ...style, left: '0%', width: `${left + width - 100}%` }, split: true },
      ];
    },
    // --- Zoom / pan ---------------------------------------------------------
    resetZoom() { this.zoom = 1; this.panX = 0; this.panY = 0; this.dragging = false; },
    // Scale by `factor`, keeping the point at screen (ax, ay) fixed. Defaults to the stage centre, so the
    // +/- buttons zoom about the middle while the wheel zooms toward the cursor.
    applyZoom(factor, ax, ay) {
      if (!this.activeUrl) return;
      const next = Math.min(Math.max(this.zoom * factor, 1), 8);
      if (next === this.zoom) return;
      const stage = this.$refs.stage;
      const rect = stage ? stage.getBoundingClientRect() : null;
      const cx = rect ? rect.left + rect.width / 2 : 0;
      const cy = rect ? rect.top + rect.height / 2 : 0;
      const ux = (ax ?? cx) - cx;   // cursor offset from the (centred) transform origin
      const uy = (ay ?? cy) - cy;
      const ratio = next / this.zoom;
      this.panX = ux * (1 - ratio) + this.panX * ratio;
      this.panY = uy * (1 - ratio) + this.panY * ratio;
      this.zoom = next;
      if (this.zoom <= 1.001) this.resetZoom();
    },
    zoomBy(factor) { this.applyZoom(factor); },
    onWheel(e) { this.applyZoom(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX, e.clientY); },
    onDragStart(e) {
      if (this.zoom <= 1 || e.button !== 0) return;
      this.dragging = true; this.dragLastX = e.clientX; this.dragLastY = e.clientY;
    },
    onDragMove(e) {
      if (!this.dragging) return;
      this.panX += e.clientX - this.dragLastX;
      this.panY += e.clientY - this.dragLastY;
      this.dragLastX = e.clientX; this.dragLastY = e.clientY;
    },
    onDragEnd() { this.dragging = false; },
    onSourceLoad(e) {
      this.natW = e.target.naturalWidth || 0;
      this.natH = e.target.naturalHeight || 0;
      this.sourceFailed = false;
      this.sourceImgReady = true;   // hide the shimmer, reveal the image + box
    },
    onSourceError() {
      this.sourceImgReady = false;
      this.sourceFailed = true;
    },
    // Load the source frame's natural size without rendering it (needed to map the box onto the
    // equirect when the user jumps straight to street view).
    preloadSourceDims() {
      if (!this.sourceUrl || (this.natW && this.natH)) return;
      const img = new Image();
      img.onload = () => { this.natW = img.naturalWidth || 0; this.natH = img.naturalHeight || 0; };
      img.src = this.sourceUrl;
    },
    show360() {
      this.mode = 'equirect';
      if (!this.equirectUrl && !this.equirectLoading) this.$emit('request-equirect');
    },
    // Map the thumbnail crop square onto the equirect from the source face's canonical world orientation
    // (see equirectProjection.js). Needs only the face label + the crop normalised by the frame's size.
    computeEquirectBox() {
      this.equirectBox = null;
      if (!this.is360 || !this.faceName || !this.cropRect || !this.natW || !this.natH) return;
      try {
        const c = this.cropRect;
        const norm = {
          left: c.left / this.natW,
          top: c.top / this.natH,
          width: c.side / this.natW,
          height: c.side / this.natH,
        };
        this.equirectBox = equirectBoxFromFace(this.faceName, norm);
      } catch {
        this.equirectBox = null;
      }
    },
    // The detection box for the CURRENT view as a normalised rect (0..1), or null if there's none to
    // draw. Equirect view: the mapped box (already 0..1). Source view: the raw box (equirect source)
    // or the thumbnail crop square (cube/mono), normalised by the source frame's size.
    activeBoxNorm() {
      if (this.mode === 'equirect') return this.equirectBox || null;
      if (this.isEquirectSource) {
        const b = this.bbox;
        if (!b || !this.natW || !this.natH) return null;
        return { left: b.left / this.natW, top: b.top / this.natH, width: b.width / this.natW, height: b.height / this.natH };
      }
      const c = this.cropRect;
      if (!c || !this.natW || !this.natH) return null;
      return { left: c.left / this.natW, top: c.top / this.natH, width: c.side / this.natW, height: c.side / this.natH };
    },
    // Download: if there's a box to draw, ask whether to burn it in; otherwise just save the image.
    downloadActive() {
      if (!this.activeUrl || this.downloading) return;
      if (this.activeBoxNorm()) this.downloadAsk = true;
      else this.doDownload(false);
    },
    // Save the image currently shown (source frame, or the equirect in street view), optionally with the
    // marker box burned onto the pixels via a canvas.
    async doDownload(withBox) {
      this.downloadAsk = false;
      const url = this.activeUrl;
      if (!url || this.downloading) return;
      this.downloading = true;
      // Reference id (work order / CMMS) wins the filename when present; otherwise the source frame name.
      const stem = this.referenceId || (this.sourceFileName || this.title || 'image').replace(/\.[^.]+$/, '');
      let safe = (this.mode === 'equirect' ? `${stem}-360` : stem)
        .replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'image';
      if (withBox) safe += '-box';
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        // Prefer the real extension from the source path/filename — Azure serves blobs as
        // application/octet-stream, so blob.type would otherwise save the file as ".octet-stream".
        // Only trust the MIME type when it's a genuine image subtype.
        const fromName = (this.sourceFileName || '').match(/\.([a-z0-9]+)$/i)?.[1];
        const fromUrl = url.split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i)?.[1];
        const mimeSub = blob.type.startsWith('image/') ? blob.type.split('/')[1].split('+')[0] : null;
        let ext = (fromName || fromUrl || mimeSub || 'jpg').toLowerCase();

        let outBlob = blob;
        if (withBox) {
          const baked = await this.bakeBox(blob).catch(() => null);
          if (baked) { outBlob = baked; ext = 'png'; }   // canvas export is PNG
        }

        const href = URL.createObjectURL(outBlob);
        const a = document.createElement('a');
        a.href = href;
        a.download = `${safe}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      } catch {
        window.open(url, '_blank', 'noopener');
      } finally {
        this.downloading = false;
      }
    },
    // Draw the current view's image onto a canvas with the marker box outlined, returning a PNG blob.
    // The box straddling the panorama ±180° fold is drawn as two pieces, matching the on-screen overlay.
    async bakeBox(blob) {
      const box = this.activeBoxNorm();
      if (!box) return null;
      const src = URL.createObjectURL(blob);
      try {
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = src;
        });
        const W = img.naturalWidth, H = img.naturalHeight;
        if (!W || !H) return null;
        const canvas = document.createElement('canvas');
        canvas.width = W; canvas.height = H;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, W, H);

        const x = box.left * W, y = box.top * H, w = box.width * W, h = box.height * H;
        const lw = Math.max(2, Math.round(Math.min(W, H) * 0.004));
        ctx.lineWidth = lw;
        ctx.strokeStyle = '#64ffda';
        ctx.lineJoin = 'round';
        const pieces = (x + w > W)   // wraps the seam → right edge + wrapped remainder on the left
          ? [[x, y, W - x, h], [0, y, x + w - W, h]]
          : [[x, y, w, h]];
        for (const [px, py, pw, ph] of pieces) {
          ctx.strokeRect(px + lw / 2, py + lw / 2, Math.max(1, pw - lw), Math.max(1, ph - lw));
        }

        // Reference id drawn as a pill under (or, if no room, above) the box, anchored to the main piece.
        if (this.referenceId) {
          const [bx, by, bw, bh] = pieces[0];
          const fontPx = Math.max(14, Math.round(Math.min(W, H) * 0.022));
          const pad = Math.round(fontPx * 0.4);
          ctx.font = `600 ${fontPx}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          const tw = ctx.measureText(this.referenceId).width;
          const pillW = tw + pad * 2, pillH = fontPx + pad * 2;
          let pillY = by + bh + lw + pad;                       // below the box
          if (pillY + pillH > H) pillY = by - pillH - lw - pad;  // flip above if it would clip
          pillY = Math.min(Math.max(pillY, 2), H - pillH - 2);
          let pillX = bx + bw / 2 - pillW / 2;                  // centred on the box
          pillX = Math.min(Math.max(pillX, 2), W - pillW - 2);
          ctx.fillStyle = 'rgba(8, 12, 15, 0.82)';
          ctx.fillRect(pillX, pillY, pillW, pillH);
          ctx.fillStyle = '#64ffda';
          ctx.fillText(this.referenceId, pillX + pad, pillY + pad);
        }

        return await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      } finally {
        URL.revokeObjectURL(src);
      }
    },
  },
};
</script>

<style scoped>
.pv-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  background: rgba(8, 12, 15, 0.92);
  backdrop-filter: blur(3px);
}

.pv-bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.pv-title { display: flex; flex-direction: column; min-width: 0; margin-right: auto; }
.pv-title-main { color: #fff; font-weight: 700; font-size: 0.98rem; line-height: 1.2; }
.pv-file { color: #8aa0aa; font-size: 0.78rem; font-family: ui-monospace, Menlo, Consolas, monospace; }

.pv-tabs { display: flex; gap: 4px; flex: 0 0 auto; }
.pv-tab {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc; font: inherit; font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.pv-tab:hover { background: rgba(255, 255, 255, 0.09); color: #fff; }
.pv-tab.active { background: rgba(100, 255, 218, 0.12); border-color: rgba(100, 255, 218, 0.4); color: #64ffda; }
.pv-peg { display: block; flex: 0 0 auto; }

.pv-bar-actions { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.pv-icon-btn {
  display: inline-flex; align-items: center; gap: 6px;
  height: 36px; padding: 0 11px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08); color: #cfd8dc;
  font: inherit; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: background 0.15s ease, color 0.15s ease;
}
.pv-icon-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); color: #fff; }
.pv-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pv-icon-btn.active { color: #64ffda; border-color: rgba(100, 255, 218, 0.35); }

.pv-close {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08); color: #e6edf0;
  cursor: pointer; transition: background 0.15s ease;
}
.pv-close:hover { background: rgba(255, 255, 255, 0.12); }

.pv-stage {
  position: relative;
  flex: 1 1 auto; min-height: 0;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  overflow: hidden;
}
.pv-stage.is-zoomed { cursor: grab; }
.pv-stage.dragging { cursor: grabbing; }

/* Zoom controls — bottom-centre of the stage. */
.pv-zoom {
  position: absolute; bottom: 14px; left: 50%; transform: translateX(-50%);
  display: flex; align-items: center; gap: 2px; padding: 3px;
  background: rgba(10, 15, 18, 0.82); border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px; backdrop-filter: blur(4px);
}
.pv-zoom-btn, .pv-zoom-level {
  display: inline-flex; align-items: center; justify-content: center;
  height: 30px; border: none; background: none; color: #e6edf0;
  font: inherit; font-size: 0.8rem; font-weight: 600; cursor: pointer; border-radius: 7px;
}
.pv-zoom-btn { width: 32px; }
.pv-zoom-level { min-width: 52px; font-variant-numeric: tabular-nums; }
.pv-zoom-btn:hover:not(:disabled), .pv-zoom-level:hover { background: rgba(255, 255, 255, 0.1); color: #64ffda; }
.pv-zoom-btn:disabled { opacity: 0.35; cursor: not-allowed; }

/* Card: matting + rounded frame + shadow around the image, so the result reads as a framed card. */
.pv-card {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 14px;
  background: #141c20;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
  max-width: 100%; max-height: 100%;
}

/* Shrink-wraps the contained image so the bbox overlay (positioned in %) lines up exactly. */
.pv-imgwrap { position: relative; display: inline-block; line-height: 0; max-width: 100%; max-height: 100%; }
.pv-img { display: block; max-width: 100%; max-height: calc(100vh - 196px); object-fit: contain; border-radius: 8px; }

/* Shimmer placeholder shown while the URL resolves + the image downloads. */
.pv-shimmer {
  width: min(72vw, 940px); height: min(62vh, 540px); max-width: 100%; border-radius: 8px;
  background:
    linear-gradient(100deg, rgba(255, 255, 255, 0.03) 30%, rgba(255, 255, 255, 0.09) 50%, rgba(255, 255, 255, 0.03) 70%),
    #121a1e;
  background-size: 200% 100%, 100% 100%;
  animation: pv-shimmer 1.3s ease-in-out infinite;
}
@keyframes pv-shimmer {
  0% { background-position: 150% 0, 0 0; }
  100% { background-position: -150% 0, 0 0; }
}

/* The finding highlight as a framed "window": a rounded teal frame over the inference region, a dark
   rim so it reads on any image, and a dimmed surround that spotlights it. Its header carries the
   class and confidence (room to add more later). */
.pv-bbox {
  position: absolute;
  border: 2.5px solid #64ffda;
  border-radius: 12px;
  box-shadow:
    0 0 0 1.5px rgba(0, 0, 0, 0.7),
    0 0 0 9999px rgba(8, 12, 15, 0.34),
    0 10px 34px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}
/* Seam-split halves: keep the rim, drop the full-screen spotlight (two would darken each other). */
.pv-bbox--split {
  box-shadow:
    0 0 0 1.5px rgba(0, 0, 0, 0.7),
    0 10px 34px rgba(0, 0, 0, 0.5);
}

.pv-empty {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
  color: #8aa0aa; font-size: 0.9rem;
}

.pv-hint {
  flex: 0 0 auto; text-align: center; color: #8aa0aa; font-size: 0.8rem;
  padding: 10px 16px 16px;
}

/* Download choice overlay */
.pv-ask {
  position: absolute; inset: 0; z-index: 10;
  display: flex; align-items: center; justify-content: center;
  background: rgba(8, 12, 15, 0.55);
}
.pv-ask-card {
  width: min(360px, 86vw);
  background: #141c20; border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px; padding: 20px; text-align: center;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
}
.pv-ask-title { color: #e6edf0; font-size: 1rem; font-weight: 600; }
.pv-ask-sub { color: #8aa0aa; font-size: 0.82rem; margin: 6px 0 16px; }
.pv-ask-actions { display: flex; gap: 10px; justify-content: center; }
.pv-ask-btn {
  flex: 1; padding: 9px 12px; border-radius: 8px; cursor: pointer;
  font-size: 0.85rem; font-weight: 500;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.12); color: #cfd8dc;
}
.pv-ask-btn:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
.pv-ask-btn.primary {
  background: rgba(100, 255, 218, 0.14); border-color: rgba(100, 255, 218, 0.4); color: #64ffda;
}
.pv-ask-btn.primary:hover { background: rgba(100, 255, 218, 0.22); }
</style>
